import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  TextField,
  Select,
  FormControl,
  Typography,
  Paper,
  Alert,
  Chip,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS, FORD_SHADOWS } from '../../theme/fordTheme.js';

// Main container - Ford theme
export const ConfigContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: FORD_COLORS.backGroundColor,
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  padding: '28px',
  border: 'none',
  // borderRadius: FORD_RADIUS.sm,
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Outer border container - Ford theme
export const ConfigOuterContainer = styled(Box)(({ theme }) => ({
  // borderRadius: FORD_RADIUS.lg,
  minHeight: 'calc(100vh - 40px)',
  boxSizing: 'border-box',
  // boxShadow: FORD_SHADOWS.md,
  width: '100%',
  maxWidth: '100vw',
  overflow: 'visible',
  // padding: '0 16px',
}));

// Page header - Ford theme (matching Dashboard exactly)
export const ConfigPageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '22px',
  '& h2': {
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    color: 'rgb(19, 34, 52)',
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide,
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    marginTop: '4px',
  }
}));

// Mode selection container - Ford theme
export const ConfigModeBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: FORD_SPACING.sm,
  marginBottom: '10px',
  flexWrap: 'wrap',
}));

// Custom styled button for mode selection - Ford theme
export const ConfigModeButton = styled(Button)(({ theme, selected }) => ({
  padding: `${FORD_SPACING.sm} ${FORD_SPACING.md}`,
  borderRadius: FORD_RADIUS.sm,
  textTransform: 'none',
  fontWeight: FORD_TYPOGRAPHY.medium,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.body2,
  border: selected ? `1px solid ${FORD_COLORS.secondary}` : `1px solid ${FORD_COLORS.textMuted}`,
  backgroundColor: selected ? FORD_COLORS.secondary : 'transparent',
  color: selected ? FORD_COLORS.textColorWhite : FORD_COLORS.textMuted,
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: selected ? FORD_SHADOWS.md : 'none',
  position: 'relative',
  overflow: 'hidden',
  minWidth: '160px',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: selected ? 0 : '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(45deg, ${FORD_COLORS.secondary}20, ${FORD_COLORS.secondary}40)`,
    transition: 'left 0.3s ease-in-out',
    zIndex: 0,
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    position: 'relative',
    zIndex: 1,
  },
  '& > span': {
    position: 'relative',
    zIndex: 1,
  },
  '&:hover': {
    backgroundColor: selected ? '#0554c7' : FORD_COLORS.primaryLight + '40',
    color: selected ? FORD_COLORS.textColorWhite : FORD_COLORS.textLight,
    // transform: 'translateY(-2px)',
    // boxShadow: selected ? FORD_SHADOWS.lg : FORD_SHADOWS.sm,
    border: `1px solid ${FORD_COLORS.secondary}`,
    '&:before': {
      left: 0,
    },
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: selected ? FORD_SHADOWS.sm : 'none',
  },
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
    transform: 'none',
    '&:before': {
      left: '-100%',
    },
  }
}));

// Panel container - Ford theme
export const ConfigPanel = styled(Paper)(({ theme }) => ({
  border: 'none',
  borderRadius: '12px',
  padding: '16px',
  backgroundColor: FORD_COLORS.backgroundford,
  color: FORD_COLORS.textLight,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  '& h3': {
    color: 'rgb(19, 34, 52)',
    fontSize: FORD_TYPOGRAPHY.body1,
    fontWeight: FORD_TYPOGRAPHY.bold,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    margin: '8px 0',
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingNormal,
  },
  '& p': {
    color: 'rgb(94, 110, 126)',
    fontSize: '12px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
    margin: '8px 0',
    lineHeight: FORD_TYPOGRAPHY.lineHeightNormal,
  }
}));

// Form grid layout - Ford theme
export const ConfigFormGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '16px 24px',
  margin: '16px 0 24px',
}));

// Custom styled text field - Ford theme
export const ConfigTextField = styled(TextField)(({ theme, error }) => ({
  '& .MuiOutlinedInput-root': {
    // backgroundColor: FORD_COLORS.surfaceDark,
    color: FORD_COLORS.textMuted,
    borderRadius: FORD_RADIUS.sm,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: '12px',
    '& fieldset': {
      borderColor: error ? FORD_COLORS.error : FORD_COLORS.primaryLight,
    },
    '&:hover fieldset': {
      borderColor: error ? FORD_COLORS.error : FORD_COLORS.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? FORD_COLORS.error : FORD_COLORS.secondary,
    },
    '&.Mui-disabled': {
      // backgroundColor: `${FORD_COLORS.backgroundDark}80`,
      color: FORD_COLORS.textMuted,
      '& fieldset': {
        borderColor: FORD_COLORS.primaryLight,
      },
      '& input': {
        color: FORD_COLORS.textMuted,
        WebkitTextFillColor: FORD_COLORS.textMuted,
      },
    },
    '& input': {
      color: FORD_COLORS.textLight,
      fontFamily: FORD_TYPOGRAPHY.newfont,
      '&::placeholder': {
        color: FORD_COLORS.textMuted,
        opacity: 0.8,
      },
      '&[readonly]': {
        // backgroundColor: `${FORD_COLORS.surfaceDark}60`,
        color: FORD_COLORS.textLight,
        cursor: 'default',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: FORD_COLORS.textMuted,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: '12px',
    '&.Mui-focused': {
      color: error ? FORD_COLORS.error : FORD_COLORS.secondary,
    },
    '&.Mui-disabled': {
      color: FORD_COLORS.textMuted,
    },
  },
  '& .MuiSelect-icon': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiMenuItem-root': {
    backgroundColor: FORD_COLORS.surfaceDark,
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    '&:hover': {
      backgroundColor: FORD_COLORS.primaryLight,
    },
  },
}));

// Custom styled select - Ford theme
export const ConfigSelect = styled(Select)(({ theme, error }) => ({
  minWidth: '150px',
  height: '40px', // Match text field height
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`,
    borderRadius: FORD_RADIUS.sm,
    height: '40px', // Ensure consistent height
    '& fieldset': {
      borderColor: `${error ? FORD_COLORS.error : FORD_COLORS.primaryLight} !important`,
    },
    '&:hover fieldset': {
      borderColor: `${error ? FORD_COLORS.error : FORD_COLORS.secondary} !important`,
    },
    '&.Mui-focused fieldset': {
      borderColor: `${error ? FORD_COLORS.error : FORD_COLORS.secondary} !important`,
    },
  },
  '& .MuiInputLabel-root': {
    color: `${FORD_COLORS.textMuted} !important`,
    fontSize: '12px',
    '&.Mui-focused': {
      color: `${error ? FORD_COLORS.error : FORD_COLORS.secondary} !important`,
    },
  },
  '& .MuiSelect-select': {
    color: `${FORD_COLORS.textLight} !important`,
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    fontFamily: `${FORD_TYPOGRAPHY.newfont} !important`,
    fontSize: '12px !important',
    padding: '8.5px 14px', // Match text field padding for small size
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    color: `${FORD_COLORS.textMuted} !important`,
  },
  '& .MuiInputBase-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`,
    height: '40px', // Consistent height
    fontFamily: `${FORD_TYPOGRAPHY.newfont} !important`,
  },
  // MenuItem styling for dropdown options
  '& .MuiMenuItem-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`,
    fontFamily: `${FORD_TYPOGRAPHY.newfont} !important`,
    fontSize: '12px !important',
    '&:hover': {
      backgroundColor: `${FORD_COLORS.primaryLight} !important`,
    },
  },
}));

// Custom styled form control for select fields - Ford theme
export const ConfigFormControl = styled(FormControl)(({ theme, error }) => ({
  '& .MuiInputLabel-root': {
    color: FORD_COLORS.textMuted,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: '12px',
    '&.Mui-focused': {
      color: error ? FORD_COLORS.error : FORD_COLORS.secondary,
    },
    '&.Mui-error': {
      color: FORD_COLORS.error,
    },
  },
  '& .MuiFormHelperText-root': {
    color: FORD_COLORS.error,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: FORD_TYPOGRAPHY.caption,
    marginLeft: 0,
    marginTop: FORD_SPACING.xs,
  },
}));

// Action buttons container - Ford theme
export const ConfigActionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: FORD_SPACING.sm,
  alignItems: 'center',
  flexWrap: 'wrap',
  marginTop: FORD_SPACING.md,
}));

// Primary action button - matching Dashboard .btn styles
export const ConfigPrimaryButton = styled(Button)(({ theme }) => ({
  padding: '8px 12px', // Match Dashboard .btn padding
  backgroundColor: '#066fef', // Match Dashboard --accent
  color: 'white',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  textTransform: 'none',
  fontWeight: 500,
  // marginRight: '10px', // Match Dashboard .btn margin-right
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    opacity: 0.7, // Match Dashboard .btn:disabled
    backgroundColor: 'lightGray',
    cursor: 'not-allowed',
  }
}));

// Secondary action button - matching Dashboard .btn styles
export const ConfigSecondaryButton = styled(Button)(({ theme }) => ({
  padding: '8px 12px', // Match Dashboard .btn padding
  backgroundColor: '#233058', // Match Dashboard --border
  color: 'white',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  textTransform: 'none',
  fontWeight: 500,
  marginRight: '10px',
  '&:hover': {
    backgroundColor: '#2a3a6b',
  },
  '&:disabled': {
    opacity: 0.7, // Match Dashboard .btn:disabled
    cursor: 'not-allowed',
  }
}));

// Ghost/outline button - matching Dashboard secondary button styles
export const ConfigGhostButton = styled(Button)(({ theme }) => ({
  padding: '8px 12px', // Match Dashboard .btn padding
  backgroundColor: 'transparent',
  color: '#9aa3c7', // Match Dashboard --muted
  border: '1px solid #233058', // Match Dashboard --border
  borderRadius: 8,
  cursor: 'pointer',
  textTransform: 'none',
  fontWeight: 500,
  marginRight: '10px',
  '&:hover': {
    backgroundColor: '#233058',
    color: '#fff',
  },
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
  }
}));

// Delete button - matching Dashboard .btn with error styling
export const ConfigDeleteButton = styled(Button)(({ theme }) => ({
  padding: '8px 12px', // Match Dashboard .btn padding
  backgroundColor: '#ef5350', // Match Dashboard .kpi.error border-color
  color: 'white',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  textTransform: 'none',
  fontWeight: 500,
  marginRight: '10px',
  '&:hover': {
    backgroundColor: '#d32f2f',
  },
  '&:disabled': {
    opacity: 0.7, // Match Dashboard .btn:disabled
    cursor: 'not-allowed',
  }
}));

// File input container - matching Dashboard .row layout
export const ConfigFileInputBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12, // Match Dashboard gap
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 10,
  '& input[type="file"]': {
    padding: 8, // Match Dashboard select padding
    // backgroundColor: '#0c142e', // Match Dashboard select background
    color: FORD_COLORS.textMuted, // Match Dashboard --text
    border: '1px solid #233058', // Match Dashboard --border
    borderRadius: 8,
    '&:disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
    }
  }
}));

// Success alert - matching Dashboard chip-ok colors
export const ConfigSuccessAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford, // Match Dashboard .chip-ok background
  color: FORD_COLORS.success, // Match Dashboard .chip-ok border-color
  border: `1px solid ${FORD_COLORS.success}`,
  borderRadius: 8,
  marginTop: 8,
  '& .MuiAlert-icon': {
    color: FORD_COLORS.success,
  }
}));

// Error alert - matching Dashboard chip-error colors
export const ConfigErrorAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford, // Match Dashboard .chip-error background
  color: FORD_COLORS.error, // Match Dashboard .chip-error border-color
  border: `1px solid ${FORD_COLORS.error}`,
  borderRadius: 8,
  marginTop: 8,
  '& .MuiAlert-icon': {
    color: FORD_COLORS.error,
  }
}));

// Info text
export const ConfigInfoText = styled(Typography)(({ theme }) => ({
  color: '#9aa3c7',
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  fontFamily: 'monospace',
  backgroundColor: 'rgba(35, 48, 88, 0.3)',
  padding: theme.spacing(1),
  borderRadius: 4,
}));

// Status chip
export const ConfigStatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return { bg: 'rgba(16, 185, 129, 0.2)', color: '#10b981' };
      case 'processed':
        return { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' };
      case 'pending':
        return { bg: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' };
      case 'rejected':
        return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
      default:
        return { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' };
    }
  };

  const colors = getStatusColor(status);

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 500,
    fontSize: '0.75rem',
  };
});

// Table container - matching Dashboard .table styles exactly
export const ConfigTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'transparent',
  marginTop: 8,
  // border: 'none',
  borderRadius: 0,
  border: '1px solid rgba(35, 48, 88, 0.5)',
  '& .MuiTable-root': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    textAlign: 'left',
    fontSize: '12px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      height: '42px',
      '& .MuiTableCell-root': {
        backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background
        color: '#ffffff', // White text
        fontWeight: 700,
        fontSize: '12px',
        padding: '8px 10px',
        textAlign: 'left',
        textTransform: 'uppercase', // Capital letters
        letterSpacing: '0.5px',
        borderBottom: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 11,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      }
    },
    '& .MuiTableBody-root': {
      position: 'relative',
      zIndex: 1,
      '& .MuiTableCell-root': {
        borderBottom: '1px solid rgba(35, 48, 88, 0.5)',
        color: FORD_COLORS.textMuted,
        fontSize: '12px',
        backgroundColor: FORD_COLORS.backgroundford,
        padding: '8px 10px',
        textAlign: 'left',
      }
    }
  }
}));

// Form field container
export const ConfigFieldContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: '220px',
  '& label': {
    color: '#e7ecff',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '4px',
  }
}));

// Load status indicator - matching Dashboard .muted.small styles
export const ConfigLoadStatus = styled(Typography)(({ theme }) => ({
  color: '#9aa3c7', // Match Dashboard --muted
  fontSize: '11px', // Match Dashboard .muted.small font-size
  fontStyle: 'italic',
  marginLeft: 8,
}));

// Section header - matching Dashboard h4 styles
export const ConfigSectionHeader = styled(Typography)(({ theme }) => ({
  color: '#e7ecff', // Match Dashboard --text
  fontSize: '1em', // Match browser default h4 size
  fontWeight: 'bold', // Match browser default
  margin: '8px 0', // Match Dashboard h2, h3, h4 margin
}));

// Template info box - matching Dashboard panel styling
export const ConfigTemplateInfo = styled(Box)(({ theme }) => ({
  // marginTop: 8, // Match Dashboard spacing
  padding: 12, // Match Dashboard panel padding
  // backgroundColor: 'rgba(30,45,90,0.18)', // Match Dashboard panel background
  borderRadius: 12, // Match Dashboard border-radius
  border: '1px solid #233058', // Match Dashboard --border
  fontFamily: FORD_TYPOGRAPHY.newfont,
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: '8px',
  '& .template-title': {
    fontSize: '16px', // Match Dashboard standard text
    fontWeight: 500,
    color: 'rgb(19, 34, 52)',
  },
  '& .template-columns': {
    color: 'rgb(138, 152, 166)',
    fontSize: '12px', // Smaller monospace text
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    marginLeft: '5px'
  }
}));

// History section - matching other UI sections with border
export const ConfigHistorySection = styled(Box)(({ theme }) => ({
  border: 'none',
 boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  borderRadius: FORD_RADIUS.lg,
  padding: FORD_SPACING.md,
  background: FORD_COLORS.backgroundford,
  marginTop: 10, // Increased spacing for section separation
  color: FORD_COLORS.textLight,
  '& h3': {
    color: 'rgb(19, 34, 52)',
    fontSize: FORD_TYPOGRAPHY.body1,
    fontWeight: FORD_TYPOGRAPHY.bold,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    margin: `${FORD_SPACING.sm} 0`,
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingNormal,
  }
}));

// Pagination container
export const ConfigPaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
}));

// Styled TablePagination component
export const ConfigTablePagination = styled(TablePagination)(({ theme }) => ({
  '& .MuiTablePagination-toolbar': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiSelect-select': {
    color: FORD_COLORS.textMuted,
    backgroundColor: 'transparent',
  },
  '& .MuiTablePagination-actions': {
    color: FORD_COLORS.textMuted,
  },
  '& .MuiIconButton-root': {
    color: FORD_COLORS.textMuted,
    '&:hover': {
      backgroundColor: FORD_COLORS.primaryLight + '20',
    },
    '&.Mui-disabled': {
      color: FORD_COLORS.textMuted + '50',
    },
  },
}));

// Job Status Dialog - Ford theme
export const ConfigJobStatusDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: FORD_COLORS.backgroundford,
    border: `1px solid ${FORD_COLORS.primaryLight}`,
    borderRadius: FORD_RADIUS.lg,
    color: FORD_COLORS.textLight,
    minWidth: '400px',
    maxWidth: '600px',
    boxShadow: FORD_SHADOWS.lg,
  },
}));

// Job Status Dialog Title - Ford theme
export const ConfigJobStatusDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background matching OfflineEventsDrawer
  color: '#ffffff',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.h3,
  fontWeight: FORD_TYPOGRAPHY.bold,
  borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
  padding: FORD_SPACING.md,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& .MuiTypography-root': {
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: FORD_TYPOGRAPHY.body1,
    fontWeight: FORD_TYPOGRAPHY.bold,
    flex: 1,
  },
  '& .close-button': {
    color: '#ffffff',
    padding: FORD_SPACING.xs,
    '&:hover': {
      backgroundColor: FORD_COLORS.primaryLight + '20',
      color: FORD_COLORS.textLight,
    },
  },
}));

// Job Status Dialog Content - Ford theme
export const ConfigJobStatusDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  color: FORD_COLORS.textLight,
  padding: FORD_SPACING.lg,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  marginTop: FORD_SPACING.xs,
  '& .MuiTypography-root': {
    color: FORD_COLORS.textLight,
    fontFamily: FORD_TYPOGRAPHY.newfont,
  },
  '& .MuiTypography-h6': {
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.bold,
    marginBottom: FORD_SPACING.sm,
    color: FORD_COLORS.textLight,
  },
  '& .MuiTypography-body1': {
    fontSize: FORD_TYPOGRAPHY.body1,
    marginBottom: FORD_SPACING.xs,
    color: FORD_COLORS.textLight,
  },
  '& .MuiTypography-body2': {
    fontSize: '12px',
    marginBottom: FORD_SPACING.xs,
    color: FORD_COLORS.textMuted,
  },
  '& .loading-container': {
    display: 'flex',
    alignItems: 'center',
    gap: FORD_SPACING.sm,
    padding: FORD_SPACING.md,
  },
  '& .error-text': {
    color: FORD_COLORS.error,
  },
  '& .status-section': {
    marginBottom: FORD_SPACING.md,
  },
  '& .counts-section': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: FORD_SPACING.md,
    marginBottom: FORD_SPACING.md,
    '& .count-item': {
      padding: FORD_SPACING.md,
      backgroundColor: FORD_COLORS.backgroundDark,
      borderRadius: FORD_RADIUS.md,
      border: `1px solid ${FORD_COLORS.primaryLight}`,
      textAlign: 'center',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        // background: `linear-gradient(90deg, ${FORD_COLORS.secondary}, ${FORD_COLORS.primary})`,
      },
      // '&:hover': {
      //   transform: 'translateY(-2px)',
      //   boxShadow: FORD_SHADOWS.md,
      //   borderColor: FORD_COLORS.secondary,
      // },
      '& .MuiTypography-body2': {
        color: FORD_COLORS.textMuted,
        fontSize: '12px',
        fontWeight: FORD_TYPOGRAPHY.medium,
        marginBottom: FORD_SPACING.xs,
        // textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
      '& .MuiTypography-h6': {
        color: FORD_COLORS.textLight,
        fontSize: '12px',
        fontWeight: FORD_TYPOGRAPHY.medium,
        margin: 0,
        lineHeight: 1.2,
      },
    },
  },
  '& .errors-section': {
    marginTop: FORD_SPACING.md,
    '& .errors-title': {
      fontSize: FORD_TYPOGRAPHY.body1,
      fontWeight: FORD_TYPOGRAPHY.bold,
      marginBottom: FORD_SPACING.sm,
      color: FORD_COLORS.textLight,
    },
  },
}));

// Job Status Error Table - Ford theme (inherits from StyledTableContainer)
export const ConfigJobStatusErrorTable = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${FORD_COLORS.primaryLight}`,
  borderRadius: FORD_RADIUS.sm,
  maxHeight: '300px',
  overflow: 'auto',
  '& .MuiTable-root': {
    width: '100%',
    borderCollapse: 'collapse',
    '& .MuiTableHead-root': {
      '& .MuiTableCell-root': {
        backgroundColor: FORD_COLORS.backgroundDark,
        color: FORD_COLORS.textLight,
        fontWeight: FORD_TYPOGRAPHY.bold,
        fontSize: '12px',
        padding: `${FORD_SPACING.xs} ${FORD_SPACING.sm}`,
        textAlign: 'left',
        borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
      }
    },
    '& .MuiTableBody-root': {
      '& .MuiTableCell-root': {
        color: FORD_COLORS.textMuted,
        fontSize: '12px',
        padding: `${FORD_SPACING.xs} ${FORD_SPACING.sm}`,
        textAlign: 'left',
        borderBottom: `1px solid ${FORD_COLORS.primaryLight}20`,
        '&:first-of-type': {
          fontWeight: FORD_TYPOGRAPHY.medium,
          color: FORD_COLORS.textLight,
        },
      }
    }
  }
}));

// Device detail row container for metadata fields (matching Dashboard pattern)
export const DeviceDetailRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  padding: '4px 0',
  alignItems: 'center',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .detail-label': {
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'rgb(138, 152, 166)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    minWidth: '80px',
  },
  '& .detail-value': {
    color: 'rgb(19, 34, 52)',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.3,
    fontFamily: FORD_TYPOGRAPHY.newfont,
  },
}));

// Status cards for upload results
export const StatusCard = styled(Box)(({ theme, variant }) => {
  const getStatusStyles = () => {
    switch (variant) {
      case 'uploaded':
        return {
          backgroundColor: '#e7f5e7',
          borderColor: '#4caf50',
          titleColor: '#2e7d32',
          countColor: '#1b5e20',
        };
      case 'processed':
        return {
          backgroundColor: '#e3f2fd',
          borderColor: '#2196f3',
          titleColor: '#1976d2',
          countColor: '#0d47a1',
        };
      case 'failed':
        return {
          backgroundColor: '#ffebee',
          borderColor: '#f44336',
          titleColor: '#d32f2f',
          countColor: '#b71c1c',
        };
      default:
        return {
          backgroundColor: '#f5f5f5',
          borderColor: '#cccccc',
          titleColor: '#666666',
          countColor: '#333333',
        };
    }
  };

  const styles = getStatusStyles();

  return {
    backgroundColor: styles.backgroundColor,
    border: `2px solid ${styles.borderColor}`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    minWidth: '120px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    '& .status-title': {
      fontSize: '16px',
      fontWeight: 700,
      color: styles.titleColor,
      marginBottom: '8px',
      letterSpacing: '0.5px',
      fontFamily: FORD_TYPOGRAPHY.newfont,
    },
    '& .status-count': {
      fontSize: '14px',
      fontWeight: 800,
      color: styles.countColor,
      lineHeight: 1.2,
      fontFamily: FORD_TYPOGRAPHY.newfont,
    },
  };
});

// Status cards container
export const StatusCardsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginTop: '16px',
  marginBottom: '16px',
  justifyContent: 'left',
  flexWrap: 'wrap',
  fontFamily: FORD_TYPOGRAPHY.newfont,
}));

// Job Status Dialog Actions - Ford theme
export const ConfigJobStatusDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  borderTop: `1px solid ${FORD_COLORS.primaryLight}`,
  padding: FORD_SPACING.md,
  justifyContent: 'flex-end',
  gap: FORD_SPACING.sm,
  '& .MuiButton-root': {
    fontFamily: FORD_TYPOGRAPHY.newfont,
    fontSize: '12px',
    fontWeight: FORD_TYPOGRAPHY.medium,
    textTransform: 'none',
    borderRadius: FORD_RADIUS.sm,
    padding: `${FORD_SPACING.xs} ${FORD_SPACING.md}`,
    minWidth: '100px',
  },
  '& .cancel-button': {
    backgroundColor: 'transparent',
    color: FORD_COLORS.textMuted,
    border: `1px solid ${FORD_COLORS.textMuted}`,
    '&:hover': {
      backgroundColor: FORD_COLORS.textMuted + '10',
      borderColor: FORD_COLORS.textLight,
      color: FORD_COLORS.textLight,
    },
  },
  '& .close-button': {
    backgroundColor: FORD_COLORS.primary,
    color: FORD_COLORS.textDark,
    border: `1px solid ${FORD_COLORS.primary}`,
    '&:hover': {
      backgroundColor: FORD_COLORS.primaryLight,
      borderColor: FORD_COLORS.primaryLight,
    },
  },
}));

// Status check button - for successful uploads
export const ConfigStatusCheckButton = styled(Button)(({ theme }) => ({
  padding: '6px 12px',
  backgroundColor: 'transparent',
  color: '#4f7cff',
  border: '1px solid #4f7cff',
  borderRadius: 8,
  cursor: 'pointer',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.75rem',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(79, 124, 255, 0.1)',
    borderColor: '#4f7cff',
  },
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
  }
}));

// In progress button - for pending uploads
export const ConfigInProgressButton = styled(Button)(({ theme }) => ({
  padding: '6px 12px',
  backgroundColor: 'transparent',
  color: '#fbbf24',
  border: '1px solid #fbbf24',
  borderRadius: 8,
  cursor: 'default',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.75rem',
  minWidth: 'auto',
  '&.Mui-disabled': {
    borderColor: '#fbbf24',
    color: '#fbbf24',
    opacity: 1
  }
}));

// Checkbox and Replace Field Container - Ford theme
export const ConfigCheckboxFieldContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gridColumn: '1 / -1',
  gap: '4px',
}));

// Checkbox Container - Ford theme
export const ConfigCheckboxContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

// Replace Field Container - Ford theme
export const ConfigReplaceFieldContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '300px', // Default max width, can be overridden
}));

// Custom styled FormControlLabel - Ford theme
export const ConfigFormControlLabel = styled('div')(({ theme }) => ({
  color: 'rgb(19, 34, 52)', // Match Ford theme text color
  margin: 0,
  '& .MuiFormControlLabel-root': {
    margin: 0,
  },
  '& .MuiFormControlLabel-label': {
    fontFamily: FORD_TYPOGRAPHY.newfont, // Ford theme font
    fontSize: '12px', // Match other form fields
    fontWeight: 500, // Match form field weight
    color: 'rgb(19, 34, 52)', // Match Ford theme text color
  },
}));
